import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MaterialModule } from '../../../../material/material.module';
import { AppColorPalette, AppColors, AppColorsService } from '../../../../shared/services/app-colors.service';
import { OrganizationColorsService } from '../../../../shared/services/organization-colors.service';

type PaletteKey = keyof AppColorPalette;
const HEX_COLOR_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

type PaletteForm = {
  primary: FormControl<string>;
  secondary: FormControl<string>;
  accent: FormControl<string>;
  background: FormControl<string>;
  surface: FormControl<string>;
  text: FormControl<string>;
};

@Component({
  selector: 'app-app-colors',
  imports: [CommonModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './app-colors.component.html',
  styleUrl: './app-colors.component.css'
})
export class AppColorsComponent implements OnInit {
  readonly paletteKeys: PaletteKey[] = ['primary', 'secondary', 'accent', 'background', 'surface', 'text'];
  readonly lightPaletteLabel = 'Light theme';
  readonly darkPaletteLabel = 'Dark theme';
  isLoading = false;

  appColorsForm = new FormGroup({
    light: new FormGroup<PaletteForm>({
      primary: new FormControl('#1976D2', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      secondary: new FormControl('#424242', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      accent: new FormControl('#82B1FF', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      background: new FormControl('#FFFFFF', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      surface: new FormControl('#FFFFFF', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      text: new FormControl('#000000', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
    }),
    dark: new FormGroup<PaletteForm>({
      primary: new FormControl('#90CAF9', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      secondary: new FormControl('#BDBDBD', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      accent: new FormControl('#82B1FF', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      background: new FormControl('#121212', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      surface: new FormControl('#1E1E1E', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
      text: new FormControl('#FFFFFF', { nonNullable: true, validators: [Validators.required, Validators.pattern(HEX_COLOR_PATTERN)] }),
    })
  });

  constructor(
    private readonly organizationColorsService: OrganizationColorsService,
    private readonly appColorsService: AppColorsService,
    private readonly snackBar: MatSnackBar,
    private readonly location: Location
  ) {}

  ngOnInit(): void {
    this.loadAppColors();
  }

  loadAppColors(): void {
    this.isLoading = true;

    this.organizationColorsService.getAppColors().subscribe({
      next: (response) => {
        const appColors = this.resolveAppColors(response);
        this.patchForm(appColors);
        this.isLoading = false;
      },
      error: () => {
        this.patchForm(this.appColorsService.getDefaultAppColors());
        this.isLoading = false;
      }
    });
  }

  save(): void {
    if (this.appColorsForm.invalid) {
      this.appColorsForm.markAllAsTouched();
      return;
    }

    const appColors = this.buildAppColorsFromForm();
    const appColorsJson = JSON.stringify(appColors);

    this.organizationColorsService.saveAppColors(appColorsJson).subscribe({
      next: () => {
        this.appColorsService.saveAndApplyAppColors(appColors, localStorage.getItem('theme'));
        this.snackBar.open('App colors saved', 'CLOSE', { duration: 2500 });
      },
      error: () => {
        this.snackBar.open('Failed to save app colors', 'CLOSE', { duration: 3000 });
      }
    });
  }

  resetToDefaults(): void {
    this.patchForm(this.appColorsService.getDefaultAppColors());
  }

  back(): void {
    this.location.back();
  }

  getPreviewJson(): string {
    return JSON.stringify(this.buildAppColorsFromForm(), null, 2);
  }

  getColorControl(theme: 'light' | 'dark', key: PaletteKey): FormControl<string> {
    return this.appColorsForm.controls[theme].controls[key];
  }

  getColorLabel(key: PaletteKey): string {
    return key.charAt(0).toUpperCase() + key.slice(1);
  }

  private resolveAppColors(response: any): AppColors {
    const candidate = response?.AppColors ?? response?.appColors ?? response;
    return this.appColorsService.parseAppColors(candidate) ?? this.appColorsService.getDefaultAppColors();
  }

  private patchForm(appColors: AppColors): void {
    this.appColorsForm.patchValue({
      light: appColors.light,
      dark: appColors.dark,
    });
  }

  private buildAppColorsFromForm(): AppColors {
    const light = this.appColorsForm.controls.light.getRawValue();
    const dark = this.appColorsForm.controls.dark.getRawValue();

    return {
      light: {
        primary: light.primary,
        secondary: light.secondary,
        accent: light.accent,
        background: light.background,
        surface: light.surface,
        text: light.text,
      },
      dark: {
        primary: dark.primary,
        secondary: dark.secondary,
        accent: dark.accent,
        background: dark.background,
        surface: dark.surface,
        text: dark.text,
      }
    };
  }
}
